import { Dashboard } from '@/common/dashboard/Dashboard';
import { DashboardCard } from '@/common/dashboard/DashboardCard';
import { FilterContainer } from '@/common/dashboard/FilterContainer';
import { DimRearrangeButton } from '@/common/entityFramework/entityPerspective/support/DimRearrangeButton';
import { DropdownSelect } from '@/common/form/formFields/DropdownSelect';
import { EntitySelect } from '@/common/form/formFields/EntitySelect';
import { useNavStateSync } from '@/common/hooks/navState/useNavStateSync';
import { ContentBox } from '@/common/layout/ContentBox';
import { Spacer, SpacerLayout } from '@/common/layout/SpacerLayout';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { Title } from '@/common/title/Title';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { McdInvTrendCard } from './McdInvTrendCard';

const title = 'Medicaid Financial Dashboard';
const titleIcon = fas.faFileInvoice;

const metricOptions = [
  { value: 'GROSS_SALE', displayName: 'Gross Sale' },
  { value: 'INV', displayName: 'Invoiced' },
  { value: 'PYMT', displayName: 'Paid' },
  { value: 'DISP', displayName: 'Disputed' },
  { value: 'DUE', displayName: 'Due' },
];

const MetricSelect = () => {
  return (
    <DropdownSelect
      fieldDef={{
        field: 'metric',
        displayName: 'Metric',
        multiSelect: true,
        values: metricOptions,
      }}
    />
  );
};

// Both pickers offer 2015Q1 (the plugin's INV_PERIOD_FLOOR) up to the SC config's current Invoice Period
// (CURR_INV_PERIOD), which the filters plugin resolves
const qtrPeriodExpr = (currInvPeriodCode) =>
  ExprUtils.and(
    ExprUtils.is('periodicity', 'QTR'),
    ExprUtils.isGreaterOrEqual('code', '2015Q1'),
    ExprUtils.isLessOrEqual('code', currInvPeriodCode),
  );
const newestFirst = [{ colId: 'code', sort: 'desc' }];

// Form values an EntitySelect expects for a preselected set of Periods; no ids means All
const periodSelection = (field, ids) =>
  ids?.length ?
    {
      [field]: ExprUtils.whereParams('id', ids),
      [field + 'SelectedCount']: ids.length,
      [field + 'SelectedIds']: ids,
    }
  : { [field]: null };

// All Entity Filter Field Definitions
const entityFilterFieldDefs = [
  {
    field: 'rbtPeriodExpr',
    displayName: 'Rebate Period',
    entity: 'PERIOD',
    code: 'RBT_PERIOD',
    module: 'sc',
    multiple: true,
    sortModel: newestFirst,
    parentFilterEntityModule: 'sc',
    parentFilterEntity: 'MCD_INV_LINE',
    parentFilterFkField: 'rbtPeriodId',
  },
  {
    field: 'earnedPeriodExpr',
    displayName: 'Earned Period',
    entity: 'PERIOD',
    code: 'EARNED_PERIOD',
    module: 'sc',
    multiple: true,
    sortModel: newestFirst,
    parentFilterEntityModule: 'sc',
    parentFilterEntity: 'MCD_INV_LINE',
    parentFilterFkField: 'earnedPeriodId',
  },
  {
    field: 'prodExpr',
    displayName: 'Product',
    entity: 'NDC11_PROD',
    code: 'NDC11_PROD',
    module: 'md',
    multiple: true,
  },
  {
    field: 'brandExpr',
    displayName: 'Brand',
    entity: 'BRAND_PROD',
    code: 'BRAND',
    module: 'md',
    multiple: true,
    hidden: true,
  },
  {
    field: 'stateExpr',
    displayName: 'State',
    entity: 'STATE',
    code: 'STATE',
    module: 'md',
    multiple: true,
    parentFilterEntityModule: 'sc',
    parentFilterEntity: 'MCD_INV_LINE',
    parentFilterFkField: 'stateId',
  },
  {
    field: 'ctrtExpr',
    displayName: 'Contract',
    entity: 'CTRT',
    code: 'CTRT',
    module: 'md',
    multiple: true,
    parentFilterEntityModule: 'sc',
    parentFilterEntity: 'MCD_INV',
    parentFilterFkField: 'ctrtId',
  },
  {
    field: 'vendorExpr',
    displayName: 'Vendor',
    entity: 'CUST',
    code: 'VENDOR',
    module: 'md',
    multiple: true,
    hidden: true,
  },
];

// All filters
const codeToFilter = Object.fromEntries(
  entityFilterFieldDefs.map((fieldDef) => [fieldDef.code, { displayName: fieldDef.displayName }]),
);

// Default filters
const defaultVisibleFilters = entityFilterFieldDefs
  .filter((fieldDef) => !fieldDef.hidden)
  .map((fieldDef) => fieldDef.code);

const fieldDefByCode = Object.fromEntries(entityFilterFieldDefs.map((fieldDef) => [fieldDef.code, fieldDef]));

const xAxisOptions = [
  {
    code: 'EARNED_PERIOD',
    displayName: 'Earned View',
    axisLabel: 'Earned Period',
    description: 'Group by the quarter the utilization occurred in.',
  },
  {
    code: 'RBT_PERIOD',
    displayName: 'Rebate View',
    axisLabel: 'Rebate Period',
    description: 'Group by the quarter the State invoiced in.',
  },
  { code: 'NDC11_PROD', displayName: 'Product View', axisLabel: 'Product', description: 'Group by NDC11.' },
  { code: 'BRAND', displayName: 'Brand View', axisLabel: 'Brand', description: 'Group by Brand.' },
  { code: 'STATE', displayName: 'State View', axisLabel: 'State', description: 'Group by State.' },
  { code: 'CTRT', displayName: 'Contract View', axisLabel: 'Contract', description: 'Group by Contract.' },
  { code: 'VENDOR', displayName: 'Vendor View', axisLabel: 'Vendor', description: 'Group by State vendor.' },
];

export const McdInvFinDashboard = () => {
  const rsrc = useRsrc();

  const defaultFormValues = useRef();
  const [dimOptions, setDimOptions] = useState();
  const [visibleFilters, setVisibleFilters] = useNavStateSync('visibleFilters', defaultVisibleFilters);

  useEffect(() => {
    rsrc.data('sc', 'MCD_INV_DASHBOARD_FILTERS_DATA_PLUGIN', {}).then((res) => {
      defaultFormValues.current = {
        metric: metricOptions.map((option) => option.value),
        measure: 'AMT',
        ctrtExpr: null,
        prodExpr: null,
        brandExpr: null,
        vendorExpr: null,
        stateExpr: null,
        // Both periods start on the most recent quarters up to CURR_INV_PERIOD
        ...periodSelection('earnedPeriodExpr', res.recentPeriodIds),
        ...periodSelection('rbtPeriodExpr', res.recentPeriodIds),
        xAxis: 'EARNED_PERIOD',
      };
      setDimOptions({
        vendorExpr: ExprUtils.whereParams('id', res.vendorCustIds),
        periodExpr: qtrPeriodExpr(res.currInvPeriodCode),
      });
    });
  }, []);

  // Saved nav state may reference filters that no longer exist; options known only at runtime are attached here
  const activeFieldDefs = useMemo(() => {
    if (!dimOptions) return [];
    const runtimeProps = {
      VENDOR: { expr: dimOptions.vendorExpr },
      RBT_PERIOD: { expr: dimOptions.periodExpr },
      EARNED_PERIOD: { expr: dimOptions.periodExpr },
    };
    return visibleFilters
      .map((code) => fieldDefByCode[code] && { ...fieldDefByCode[code], ...runtimeProps[code] })
      .filter(Boolean);
  }, [visibleFilters, dimOptions]);

  const resetDims = () => {
    setVisibleFilters(defaultVisibleFilters);
  };

  // The Dashboard's Form can only mount once its defaults are known, so until then show the card loading in place
  if (!dimOptions) {
    return (
      <ContentBox>
        <Title icon={titleIcon} title={title} />
        <SpacerLayout>
          <Spacer weights={[2, 1]}>
            <DashboardCard />
          </Spacer>
        </SpacerLayout>
      </ContentBox>
    );
  }

  const augButtons = [
    <DimRearrangeButton
      key="filterRearrangeButton"
      setDims={setVisibleFilters}
      codeToDim={codeToFilter}
      activeDims={activeFieldDefs.map(({ code }) => ({ code }))}
    />,
  ];

  return (
    <Dashboard
      title={title}
      titleIcon={titleIcon}
      defaultFormValues={defaultFormValues.current}
      augButtons={augButtons}
      onReset={resetDims}
    >
      <FilterContainer>
        <MetricSelect />
        {activeFieldDefs.map((fieldDef) => (
          <EntitySelect key={fieldDef.field} fieldDef={{ ...fieldDef, variant: 'outlined' }} />
        ))}
      </FilterContainer>
      <SpacerLayout>
        <Spacer weights={[2, 1]}>
          <McdInvTrendCard xAxisOptions={xAxisOptions} metricOptions={metricOptions} />
        </Spacer>
      </SpacerLayout>
    </Dashboard>
  );
};
